import { DeleteOutlined, PlusOutlined, SyncOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n, type MessageKey } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { SkuSelect } from "../catalog/mapping";
import { PlatformLabel } from "../channels/PlatformBadge";
import { usePoll } from "@/components/tabs/WorkTabs";

type Settings = Schemas["StockSyncSettings"];
type Rule = Schemas["SkuSyncRule"];
type Campaign = Schemas["StockCampaign"];
type Tab = "stores" | "rules" | "campaigns" | "check";

/**
 * Sinkron stok (Plan §6.3): what every store is told of the stock. Shares
 * per store, SKU rules, campaigns that keep stock for one store, and a check
 * of what each store sees for an SKU and why.
 */
export function StockSync() {
  const { t } = useI18n();
  const [tab, setTab] = useUrlFilter<Tab>("tab", "stores", ["stores", "rules", "campaigns", "check"]);
  return (
    <>
      <Space orientation="vertical" size={0} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("sync.title")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("sync.subtitle")}</Typography.Text>
      </Space>
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as Tab)}
        items={[
          { key: "stores", label: t("sync.tab.stores"), children: <StoresTab /> },
          { key: "rules", label: t("sync.tab.rules"), children: <RulesTab /> },
          { key: "campaigns", label: t("sync.tab.campaigns"), children: <CampaignsTab /> },
          { key: "check", label: t("sync.tab.check"), children: <CheckTab /> },
        ]}
      />
    </>
  );
}

function useCanManage() {
  const { can } = useAuth();
  return can("inventory.manage") || can("channels.manage");
}

function PushNowButton({ connectionId, size }: { connectionId?: string; size?: "small" }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const push = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/stock-sync/push", { body: connectionId ? { connectionId } : {} })),
    onSuccess: (r) => void message.success(t("sync.pushed", { n: r.queued })),
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Button size={size} icon={<SyncOutlined />} loading={push.isPending} onClick={() => push.mutate()}>
      {t(connectionId ? "sync.pushStore" : "sync.pushAll")}
    </Button>
  );
}

// ---- stores --------------------------------------------------------------------------

function StoresTab() {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const manage = useCanManage();
  const q = useQuery({ queryKey: ["stock-sync", "settings"], queryFn: () => unwrap(api.GET("/v1/stock-sync")) });
  const [mode, setMode] = useState<Settings["mode"]>("shared");
  const [pct, setPct] = useState<Record<string, number>>({});
  const [safety, setSafety] = useState<Record<string, number>>({});
  const [loaded, setLoaded] = useState<Settings>();
  if (q.data && q.data !== loaded) {
    setLoaded(q.data);
    setMode(q.data.mode);
    setPct(Object.fromEntries(q.data.stores.map((s) => [s.connectionId, s.percent])));
    setSafety(Object.fromEntries(q.data.warehouses.map((w) => [w.warehouseId, w.safetyStock])));
  }
  const stores = q.data?.stores ?? [];
  const warehouses = [...new Map(stores.map((s) => [s.warehouseId ?? "", s.warehouseName])).entries()];
  const sumOf = (wh: string) => stores.filter((s) => (s.warehouseId ?? "") === wh).reduce((a, s) => a + (pct[s.connectionId] ?? 100), 0);
  const over = mode === "split" && warehouses.some(([wh]) => sumOf(wh) > 100);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT("/v1/stock-sync", {
          body: {
            mode,
            stores: stores.map((s) => ({ connectionId: s.connectionId, percent: pct[s.connectionId] ?? 100 })),
            warehouses: Object.entries(safety).map(([warehouseId, safetyStock]) => ({ warehouseId, safetyStock })),
          },
        }),
      ),
    onSuccess: (x) => {
      queryClient.setQueryData(["stock-sync", "settings"], x);
      void message.success(t("sync.saved"));
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const evenly = () => {
    const next = { ...pct };
    for (const [wh] of warehouses) {
      const group = stores.filter((s) => (s.warehouseId ?? "") === wh);
      const each = Math.floor(100 / Math.max(group.length, 1));
      group.forEach((s, i) => (next[s.connectionId] = i === 0 ? 100 - each * (group.length - 1) : each));
    }
    setPct(next);
  };
  return (
    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
      <Card size="small">
        <Space orientation="vertical" size={8}>
          <Segmented<Settings["mode"]>
            value={mode}
            onChange={(m) => setMode(m)}
            disabled={!manage}
            options={[
              { value: "shared", label: t("sync.mode.shared") },
              { value: "split", label: t("sync.mode.split") },
            ]}
          />
          <Typography.Text type="secondary">{t(`sync.modeHelp.${mode}`)}</Typography.Text>
        </Space>
      </Card>
      {over && <Alert type="error" showIcon title={t("sync.overSplit")} />}
      <Table
        rowKey="connectionId"
        loading={q.isPending}
        dataSource={stores}
        pagination={false}
        locale={{ emptyText: t("sync.noStores") }}
        columns={[
          {
            title: t("orders.store"),
            render: (_, s) => (
              <Space>
                <PlatformLabel platform={s.platform} />
                <Typography.Text>{s.name}</Typography.Text>
              </Space>
            ),
          },
          { title: t("orders.warehouse"), dataIndex: "warehouseName", render: (v: string) => v || "—" },
          {
            title: t(mode === "split" ? "sync.col.share" : "sync.col.percent"),
            width: 200,
            render: (_, s) => (
              <InputNumber
                min={0}
                max={100}
                precision={0}
                suffix="%"
                disabled={!manage}
                value={pct[s.connectionId] ?? 100}
                onChange={(v) => setPct((x) => ({ ...x, [s.connectionId]: v ?? 0 }))}
                style={{ width: 120 }}
              />
            ),
          },
          ...(mode === "split"
            ? [
                {
                  title: t("sync.col.warehouseTotal"),
                  width: 170,
                  render: (_: unknown, s: Settings["stores"][number]) => {
                    const sum = sumOf(s.warehouseId ?? "");
                    return <Tag color={sum > 100 ? "error" : sum === 100 ? "success" : "warning"}>{sum}%</Tag>;
                  },
                },
              ]
            : []),
          { title: "", width: 180, render: (_, s) => manage && <PushNowButton size="small" connectionId={s.connectionId} /> },
        ]}
      />
      <Card size="small" title={t("sync.safety.title")}>
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Text type="secondary">{t("sync.safety.help")}</Typography.Text>
          <Flex gap={16} wrap>
            {(q.data?.warehouses ?? []).map((w) => (
              <Space key={w.warehouseId}>
                <Typography.Text>{w.name}</Typography.Text>
                <InputNumber
                  min={0}
                  max={1_000_000}
                  precision={0}
                  suffix="pcs"
                  disabled={!manage}
                  value={safety[w.warehouseId] ?? 0}
                  onChange={(v) => setSafety((x) => ({ ...x, [w.warehouseId]: v ?? 0 }))}
                  style={{ width: 130 }}
                />
              </Space>
            ))}
          </Flex>
        </Space>
      </Card>
      {manage && (
        <Flex gap={8} wrap>
          <Button type="primary" loading={save.isPending} disabled={over} onClick={() => save.mutate()}>
            {t("common.save")}
          </Button>
          {mode === "split" && <Button onClick={evenly}>{t("sync.evenly")}</Button>}
          <div style={{ flex: 1 }} />
          <PushNowButton />
        </Flex>
      )}
    </Space>
  );
}

// ---- SKU rules -------------------------------------------------------------------------

function ruleText(t: (k: MessageKey, v?: Record<string, string | number>) => string, r: { mode: Rule["mode"]; value: number }) {
  if (r.mode === "off") return t("sync.rule.offText");
  if (r.mode === "fixed") return t("sync.rule.fixedText", { n: r.value });
  return t("sync.rule.percentText", { n: r.value });
}

function RulesTab() {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const manage = useCanManage();
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const list = useQuery({
    queryKey: ["stock-sync", "rules", q],
    queryFn: () => unwrap(api.GET("/v1/stock-sync/rules", { params: { query: q ? { q } : {} } })),
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/stock-sync/rules/{ruleID}", { params: { path: { ruleID: id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["stock-sync", "rules"] }),
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
      <Flex gap={12} wrap justify="space-between">
        <Input.Search allowClear placeholder={t("prod.search")} onSearch={(v) => setQ(v.trim())} style={{ width: 300 }} />
        {manage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
            {t("sync.rule.add")}
          </Button>
        )}
      </Flex>
      <Typography.Text type="secondary">{t("sync.rule.help")}</Typography.Text>
      <Table<Rule>
        rowKey="id"
        loading={list.isPending}
        dataSource={list.data?.items ?? []}
        pagination={false}
        locale={{ emptyText: t("sync.rule.empty") }}
        columns={[
          {
            title: t("prod.sku"),
            render: (_, r) => (
              <Space orientation="vertical" size={0}>
                <Typography.Text strong className="z-num">
                  {r.skuCode}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {r.skuName}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t("orders.store"),
            render: (_, r) => (r.connectionId ? <Space>{r.shopName}</Space> : <Tag>{t("sync.rule.allStores")}</Tag>),
          },
          {
            title: t("sync.rule.col"),
            render: (_, r) => <Tag color={r.mode === "off" ? "default" : r.mode === "fixed" ? "purple" : "blue"}>{ruleText(t, r)}</Tag>,
          },
          { title: t("as.note"), dataIndex: "note", render: (v: string) => v || "—" },
          { title: t("prod.col.updated"), width: 200, render: (_, r) => `${r.updatedName} · ${formatDateTime(r.updatedAt, me?.workspace?.timezone)}` },
          {
            title: "",
            width: 60,
            render: (_, r) =>
              manage && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    modal.confirm({
                      title: t("sync.rule.remove"),
                      content: t("sync.rule.removeText", { sku: r.skuCode }),
                      okButtonProps: { danger: true },
                      okText: t("common.delete"),
                      cancelText: t("common.cancel"),
                      onOk: () => remove.mutateAsync(r.id),
                    })
                  }
                />
              ),
          },
        ]}
      />
      <RuleModal open={adding} onClose={() => setAdding(false)} />
    </Space>
  );
}

type RuleValues = { skuId: string; connectionId: string; mode: Rule["mode"]; value?: number; note?: string };

function RuleModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["stock-sync", "settings"], queryFn: () => unwrap(api.GET("/v1/stock-sync")) });
  const [form] = Form.useForm<RuleValues>();
  const mode = Form.useWatch("mode", form);
  const save = useMutation({
    mutationFn: (v: RuleValues) =>
      unwrap(
        api.PUT("/v1/stock-sync/rules", {
          body: { skuId: v.skuId, connectionId: v.connectionId || null, mode: v.mode, value: v.mode === "off" ? 0 : (v.value ?? 0), note: v.note ?? "" },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["stock-sync", "rules"] });
      void message.success(t("sync.saved"));
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={open}
      title={t("sync.rule.add")}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      destroyOnHidden
      afterClose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ connectionId: "", mode: "fixed" }} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="skuId" label={t("prod.sku")} rules={[{ required: true }]}>
          <SkuSelect />
        </Form.Item>
        <Form.Item name="connectionId" label={t("orders.store")} extra={t("sync.rule.storeHelp")}>
          <Select
            options={[
              { value: "", label: t("sync.rule.allStores") },
              ...(settings.data?.stores ?? []).map((s) => ({ value: s.connectionId, label: s.name })),
            ]}
          />
        </Form.Item>
        <Form.Item name="mode" label={t("sync.rule.col")}>
          <Radio.Group
            orientation="vertical"
            options={(["fixed", "percent", "off"] as const).map((m) => ({ value: m, label: t(`sync.rule.mode.${m}`) }))}
          />
        </Form.Item>
        {mode !== "off" && (
          <Form.Item name="value" label={t(mode === "percent" ? "sync.rule.percent" : "sync.rule.fixed")} rules={[{ required: true }]}>
            <InputNumber min={0} max={mode === "percent" ? 100 : 1000000} precision={0} suffix={mode === "percent" ? "%" : undefined} style={{ width: 200 }} />
          </Form.Item>
        )}
        <Form.Item name="note" label={t("as.note")}>
          <Input maxLength={200} />
        </Form.Item>
        {mode === "fixed" && <Alert type="warning" showIcon title={t("sync.rule.fixedWarn")} />}
      </Form>
    </Modal>
  );
}

// ---- campaigns ----------------------------------------------------------------------------

const stateColor: Record<Campaign["state"], string> = { scheduled: "processing", active: "success", ended: "default", cancelled: "default" };

function CampaignsTab() {
  const { t, formatDateTime, formatNumber } = useI18n();
  const { me } = useAuth();
  const tz = me?.workspace?.timezone;
  const manage = useCanManage();
  const [creating, setCreating] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({ queryKey: ["stock-sync", "campaigns"], queryFn: () => unwrap(api.GET("/v1/stock-sync/campaigns")), refetchInterval: usePoll(60_000) });
  return (
    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
      <Flex justify="space-between" gap={12} wrap>
        <Typography.Text type="secondary" style={{ maxWidth: 720 }}>
          {t("sync.camp.help")}
        </Typography.Text>
        {manage && (
          <Space wrap>
            <Button icon={<PlusOutlined />} onClick={() => setCreating(true)}>
              {t("sync.camp.add")}
            </Button>
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => setPromoting(true)}>
              {t("sync.promo.add")}
            </Button>
          </Space>
        )}
      </Flex>
      <Table<Campaign>
        rowKey="id"
        loading={list.isPending}
        dataSource={list.data?.items ?? []}
        pagination={false}
        locale={{ emptyText: t("sync.camp.empty") }}
        onRow={(c) => ({ onClick: () => setOpen(c.id), style: { cursor: "pointer" } })}
        columns={[
          {
            title: t("sync.camp.name"),
            render: (_, c) => (
              <Space orientation="vertical" size={2}>
                <Typography.Text strong>{c.name}</Typography.Text>
                <Space size={4}>
                  {c.promoType ? <Tag color="magenta">{t(`sync.promo.type.${c.promoType}`)}</Tag> : <Tag>{t("sync.camp.manual")}</Tag>}
                  {!c.locks && <Tag>{t("sync.promo.noLock")}</Tag>}
                </Space>
              </Space>
            ),
          },
          {
            title: t("orders.store"),
            render: (_, c) => (
              <Space>
                <PlatformLabel platform={c.platform} />
                {c.shopName}
              </Space>
            ),
          },
          { title: t("sync.camp.period"), render: (_, c) => `${formatDateTime(c.startsAt, tz)} – ${formatDateTime(c.endsAt, tz)}` },
          { title: t("sync.camp.locked"), width: 160, render: (_, c) => t("sync.camp.lockedN", { units: formatNumber(c.units), skus: c.skuCount }) },
          { title: t("common.status"), width: 130, render: (_, c) => <Tag color={stateColor[c.state]}>{t(`sync.camp.state.${c.state}`)}</Tag> },
        ]}
      />
      <CampaignModal open={creating} onClose={() => setCreating(false)} onCreated={(id) => setOpen(id)} />
      <PromotionModal open={promoting} onClose={() => setPromoting(false)} onCreated={(id) => setOpen(id)} />
      <CampaignDrawer id={open} onClose={() => setOpen(null)} />
    </Space>
  );
}

type CampaignValues = { name: string; connectionId: string; period: [Dayjs, Dayjs]; note?: string; lines: { skuId?: string; quantity?: number }[] };

function CampaignModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useI18n();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["stock-sync", "settings"], queryFn: () => unwrap(api.GET("/v1/stock-sync")) });
  const [form] = Form.useForm<CampaignValues>();
  const create = useMutation({
    mutationFn: (v: CampaignValues) =>
      unwrap(
        api.POST("/v1/stock-sync/campaigns", {
          body: {
            name: v.name.trim(),
            connectionId: v.connectionId,
            startsAt: v.period[0].toISOString(),
            endsAt: v.period[1].toISOString(),
            note: v.note ?? "",
            lines: v.lines.filter((l) => l.skuId && l.quantity).map((l) => ({ skuId: l.skuId!, quantity: l.quantity! })),
          },
        }),
      ),
    onSuccess: (c) => {
      void queryClient.invalidateQueries({ queryKey: ["stock-sync", "campaigns"] });
      onClose();
      onCreated(c.id);
      if (c.warnings.length > 0) {
        modal.warning({
          title: t("sync.camp.overTitle"),
          content: (
            <ul style={{ margin: 0, paddingInlineStart: 18 }}>
              {c.warnings.map((w) => (
                <li key={w.skuId}>{t("sync.camp.overLine", { sku: w.skuCode, locked: w.locked, available: w.available })}</li>
              ))}
            </ul>
          ),
        });
      } else void message.success(t("sync.camp.created"));
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={open}
      title={t("sync.camp.add")}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("sync.camp.lock")}
      cancelText={t("common.cancel")}
      confirmLoading={create.isPending}
      width={680}
      destroyOnHidden
      afterClose={() => form.resetFields()}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ period: [dayjs(), dayjs().add(1, "day")], lines: [{}] }}
        onFinish={(v) => create.mutate(v)}
      >
        <Form.Item name="name" label={t("sync.camp.name")} rules={[{ required: true, whitespace: true, max: 100 }]}>
          <Input placeholder={t("sync.camp.namePlaceholder")} />
        </Form.Item>
        <Space wrap align="start">
          <Form.Item name="connectionId" label={t("orders.store")} rules={[{ required: true }]}>
            <Select style={{ width: 240 }} options={(settings.data?.stores ?? []).map((s) => ({ value: s.connectionId, label: s.name }))} />
          </Form.Item>
          <Form.Item name="period" label={t("sync.camp.period")} rules={[{ required: true }]}>
            <DatePicker.RangePicker showTime format="DD/MM/YYYY HH:mm" />
          </Form.Item>
        </Space>
        <Typography.Text strong>{t("sync.camp.lines")}</Typography.Text>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <Space orientation="vertical" style={{ width: "100%", marginTop: 8 }}>
              {fields.map((f) => (
                <Flex key={f.key} gap={8}>
                  <Form.Item name={[f.name, "skuId"]} noStyle rules={[{ required: true }]}>
                    <SkuSelect />
                  </Form.Item>
                  <Form.Item name={[f.name, "quantity"]} noStyle rules={[{ required: true }]}>
                    <InputNumber min={1} precision={0} placeholder={t("sync.camp.qty")} style={{ width: 130 }} />
                  </Form.Item>
                  <Button type="text" danger icon={<DeleteOutlined />} disabled={fields.length <= 1} onClick={() => remove(f.name)} />
                </Flex>
              ))}
              <Button icon={<PlusOutlined />} onClick={() => add({})}>
                {t("sync.camp.addLine")}
              </Button>
            </Space>
          )}
        </Form.List>
        <Form.Item name="note" label={t("as.note")} style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function CampaignDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t, formatDateTime, formatMoney } = useI18n();
  const { me } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const manage = useCanManage();
  const tz = me?.workspace?.timezone;
  const q = useQuery({
    queryKey: ["stock-sync", "campaign", id],
    queryFn: () => unwrap(api.GET("/v1/stock-sync/campaigns/{campaignID}", { params: { path: { campaignID: id! } } })),
    enabled: !!id,
  });
  const cancel = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/stock-sync/campaigns/{campaignID}/cancel", { params: { path: { campaignID: id! } } })),
    onSuccess: (c) => {
      queryClient.setQueryData(["stock-sync", "campaign", id], c);
      void queryClient.invalidateQueries({ queryKey: ["stock-sync", "campaigns"] });
      void message.success(t("sync.camp.cancelled"));
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const c = q.data;
  return (
    <Drawer
      open={!!id}
      onClose={onClose}
      size="large"
      title={c?.name}
      extra={
        manage &&
        c &&
        (c.state === "active" || c.state === "scheduled") && (
          <Button
            danger
            loading={cancel.isPending}
            onClick={() =>
              modal.confirm({
                title: t("sync.camp.cancel"),
                content: t("sync.camp.cancelText"),
                okButtonProps: { danger: true },
                okText: t("sync.camp.cancel"),
                cancelText: t("common.close"),
                onOk: () => cancel.mutateAsync(),
              })
            }
          >
            {t("sync.camp.cancel")}
          </Button>
        )
      }
    >
      {c && (
        <Space orientation="vertical" size={16} style={{ width: "100%" }}>
          <Descriptions size="small" column={1}>
            <Descriptions.Item label={t("orders.store")}>
              <Space>
                <PlatformLabel platform={c.platform} />
                {c.shopName}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t("sync.camp.period")}>
              {formatDateTime(c.startsAt, tz)} – {formatDateTime(c.endsAt, tz)}
            </Descriptions.Item>
            <Descriptions.Item label={t("common.status")}>
              <Space>
                <Tag color={stateColor[c.state]}>{t(`sync.camp.state.${c.state}`)}</Tag>
                {c.platformStatus && <Typography.Text type="secondary" className="z-num">{c.platformStatus}</Typography.Text>}
              </Space>
            </Descriptions.Item>
            {c.promoType && (
              <Descriptions.Item label={t("sync.promo.type")}>
                <Space>
                  <Tag color="magenta">{t(`sync.promo.type.${c.promoType}`)}</Tag>
                  {c.externalId && <Typography.Text type="secondary" className="z-num">{c.externalId}</Typography.Text>}
                  {!c.locks && <Tag>{t("sync.promo.noLock")}</Tag>}
                </Space>
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t("as.by")}>
              {c.createdName} · {formatDateTime(c.createdAt, tz)}
            </Descriptions.Item>
            {c.note && <Descriptions.Item label={t("as.note")}>{c.note}</Descriptions.Item>}
          </Descriptions>
          <Table
            size="small"
            rowKey="skuId"
            pagination={false}
            dataSource={c.lines}
            columns={[
              {
                title: t("prod.sku"),
                render: (_, l) => (
                  <span>
                    <Typography.Text strong className="z-num">
                      {l.skuCode}
                    </Typography.Text>
                    <Typography.Text type="secondary"> · {l.skuName}</Typography.Text>
                  </span>
                ),
              },
              ...(c.promoType
                ? [
                    {
                      title: t("sync.promo.offer"),
                      width: 130,
                      render: (_: unknown, l: Campaign["lines"][number]) =>
                        l.promoPrice != null ? formatMoney(l.promoPrice) : l.discount != null ? `-${l.discount}%` : "—",
                    },
                    { title: t("sync.promo.perUser"), width: 100, align: "right" as const, render: (_: unknown, l: Campaign["lines"][number]) => l.perUser ?? "—" },
                    { title: t("sync.promo.used"), width: 110, align: "right" as const, render: (_: unknown, l: Campaign["lines"][number]) => l.promoUsed ?? "—" },
                  ]
                : []),
              { title: t(c.promoType ? "sync.promo.quota" : "sync.camp.qty"), dataIndex: "quantity", width: 90, align: "right" },
              { title: t("sync.camp.sold"), dataIndex: "sold", width: 90, align: "right" },
              { title: t("sync.camp.remaining"), dataIndex: "remaining", width: 110, align: "right" },
            ]}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("sync.camp.soldHelp")}
          </Typography.Text>
        </Space>
      )}
    </Drawer>
  );
}

type PromoValues = {
  name: string;
  connectionId: string;
  type: "discount" | "flash_sale" | "fixed_price";
  period: [Dayjs, Dayjs];
  lockStock: boolean;
  note?: string;
  lines: { skuId?: string; quantity?: number; discount?: number; price?: number; perUser?: number }[];
};

/**
 * Buat promosi TikTok: a discount, flash sale or fixed price made on the
 * marketplace, its quotas kept for the store while it runs (unless turned off).
 */
function PromotionModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["stock-sync", "settings"], queryFn: () => unwrap(api.GET("/v1/stock-sync")) });
  const stores = (settings.data?.stores ?? []).filter((s) => s.platform === "tiktok" || s.platform === "tokopedia");
  const [form] = Form.useForm<PromoValues>();
  const type = Form.useWatch("type", form);
  const create = useMutation({
    mutationFn: (v: PromoValues) =>
      unwrap(
        api.POST("/v1/stock-sync/promotions", {
          body: {
            name: v.name.trim(),
            connectionId: v.connectionId,
            type: v.type,
            startsAt: v.period[0].toISOString(),
            endsAt: v.period[1].toISOString(),
            lockStock: v.lockStock,
            note: v.note ?? "",
            lines: v.lines
              .filter((l) => l.skuId && l.quantity)
              .map((l) => ({
                skuId: l.skuId!,
                quantity: l.quantity!,
                discount: v.type === "fixed_price" ? null : (l.discount ?? null),
                price: v.type === "fixed_price" ? (l.price ?? null) : null,
                perUser: l.perUser ?? null,
              })),
          },
        }),
      ),
    onSuccess: (c) => {
      void queryClient.invalidateQueries({ queryKey: ["stock-sync", "campaigns"] });
      void message.success(t("sync.promo.created"));
      onClose();
      onCreated(c.id);
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={open}
      title={t("sync.promo.add")}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("sync.promo.create")}
      cancelText={t("common.cancel")}
      confirmLoading={create.isPending}
      width={760}
      destroyOnHidden
      afterClose={() => form.resetFields()}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ type: "flash_sale", lockStock: true, period: [dayjs().add(10, "minute"), dayjs().add(1, "day")], lines: [{}] }}
        onFinish={(v) => create.mutate(v)}
      >
        <Form.Item name="name" label={t("sync.camp.name")} rules={[{ required: true, whitespace: true, max: 100 }]}>
          <Input placeholder={t("sync.camp.namePlaceholder")} />
        </Form.Item>
        <Space wrap align="start">
          <Form.Item name="connectionId" label={t("orders.store")} rules={[{ required: true }]} extra={stores.length === 0 ? t("sync.promo.noStore") : undefined}>
            <Select style={{ width: 220 }} options={stores.map((s) => ({ value: s.connectionId, label: s.name }))} />
          </Form.Item>
          <Form.Item name="type" label={t("sync.promo.type")}>
            <Radio.Group
              optionType="button"
              options={(["flash_sale", "discount", "fixed_price"] as const).map((x) => ({ value: x, label: t(`sync.promo.type.${x}`) }))}
            />
          </Form.Item>
        </Space>
        <Space wrap align="start">
          <Form.Item name="period" label={t("sync.camp.period")} rules={[{ required: true }]}>
            <DatePicker.RangePicker showTime format="DD/MM/YYYY HH:mm" />
          </Form.Item>
          <Form.Item name="lockStock" label={t("sync.promo.lock")} valuePropName="checked" extra={t("sync.promo.lockHelp")}>
            <Switch />
          </Form.Item>
        </Space>
        <Typography.Text strong>{t("sync.promo.lines")}</Typography.Text>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <Space orientation="vertical" style={{ width: "100%", marginTop: 8 }}>
              {fields.map((f) => (
                <Flex key={f.key} gap={8} wrap>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Form.Item name={[f.name, "skuId"]} noStyle rules={[{ required: true }]}>
                      <SkuSelect />
                    </Form.Item>
                  </div>
                  {type === "fixed_price" ? (
                    <Form.Item name={[f.name, "price"]} noStyle rules={[{ required: true }]}>
                      <InputNumber min={1} precision={0} prefix="Rp" placeholder={t("sync.promo.price")} style={{ width: 150 }} />
                    </Form.Item>
                  ) : (
                    <Form.Item name={[f.name, "discount"]} noStyle rules={[{ required: true }]}>
                      <InputNumber min={1} max={99} precision={0} suffix="%" placeholder={t("sync.promo.discount")} style={{ width: 120 }} />
                    </Form.Item>
                  )}
                  <Form.Item name={[f.name, "quantity"]} noStyle rules={[{ required: true }]}>
                    <InputNumber min={1} precision={0} placeholder={t("sync.promo.quota")} style={{ width: 110 }} />
                  </Form.Item>
                  <Form.Item name={[f.name, "perUser"]} noStyle>
                    <InputNumber min={1} max={1000} precision={0} placeholder={t("sync.promo.perUser")} style={{ width: 120 }} />
                  </Form.Item>
                  <Button type="text" danger icon={<DeleteOutlined />} disabled={fields.length <= 1} onClick={() => remove(f.name)} />
                </Flex>
              ))}
              <Button icon={<PlusOutlined />} onClick={() => add({})}>
                {t("sync.camp.addLine")}
              </Button>
            </Space>
          )}
        </Form.List>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
          {t("sync.promo.help")}
        </Typography.Paragraph>
        <Form.Item name="note" label={t("as.note")}>
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---- check one SKU -----------------------------------------------------------------------

function CheckTab() {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const [sku, setSku] = useUrlFilter<string>("sku", "");
  const q = useQuery({
    queryKey: ["stock-sync", "preview", sku],
    queryFn: () => unwrap(api.GET("/v1/stock-sync/preview", { params: { query: { skuId: sku } } })),
    enabled: !!sku,
  });
  const why = (x: { reason: string; percent: number; locked: number; elsewhere: number; safety: number; skip: boolean }) => {
    const parts: string[] = [];
    if (x.reason === "rule_off") return t("sync.why.off");
    if (x.reason === "rule_fixed") return t("sync.why.fixed");
    if (x.reason === "rule_percent") parts.push(t("sync.why.rulePercent", { n: x.percent }));
    if (x.reason === "share" && x.percent !== 100) parts.push(t("sync.why.share", { n: x.percent }));
    if (x.reason === "split") parts.push(t("sync.why.split", { n: x.percent }));
    if (x.safety > 0) parts.push(t("sync.why.safety", { n: x.safety }));
    if (x.elsewhere > 0) parts.push(t("sync.why.elsewhere", { n: x.elsewhere }));
    if (x.locked > 0) parts.push(t("sync.why.locked", { n: x.locked }));
    return parts.length ? parts.join(" · ") : t("sync.why.all");
  };
  return (
    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
      <div style={{ maxWidth: 420 }}>
        <SkuSelect value={sku || undefined} onChange={setSku} placeholder={t("sync.check.pick")} />
      </div>
      {!sku ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("sync.check.help")} />
      ) : (
        <Table
          rowKey="channelSkuId"
          loading={q.isFetching}
          dataSource={q.data?.items ?? []}
          pagination={false}
          locale={{ emptyText: t("sync.check.none") }}
          columns={[
            {
              title: t("orders.store"),
              render: (_, x) => (
                <Space orientation="vertical" size={0}>
                  <Space>
                    <PlatformLabel platform={x.platform} />
                    {x.shopName}
                  </Space>
                  {x.variant && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {x.variant}
                    </Typography.Text>
                  )}
                </Space>
              ),
            },
            { title: t("sync.check.available"), dataIndex: "available", width: 110, align: "right" },
            {
              title: t("sync.check.told"),
              width: 120,
              align: "right",
              render: (_, x) => (x.skip ? <Tag>{t("sync.check.notSynced")}</Tag> : <Typography.Text strong>{x.quantity}</Typography.Text>),
            },
            { title: t("sync.check.why"), render: (_, x) => why(x) },
            {
              title: t("sync.check.lastPush"),
              width: 220,
              render: (_, x) =>
                x.lastPushError ? (
                  <Typography.Text type="danger">{x.lastPushError}</Typography.Text>
                ) : x.lastPushedAt ? (
                  `${x.lastPushedQty ?? "—"} · ${formatDateTime(x.lastPushedAt, me?.workspace?.timezone)}`
                ) : (
                  "—"
                ),
            },
          ]}
        />
      )}
    </Space>
  );
}
